import { useCallback, useEffect, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { LoadingIndicator } from "components/general/LoadingIndicator"

import { useProps } from "contexts/PropsContext"

import { AdminTokenService } from "services/token/AdminTokenService"
import { TokenAnalytics } from "services/token/type"

import { formatDecimalString, formatUsdCents, Translate } from "../helpers/token.helpers"
import { useTokenGate } from "../helpers/useTokenGate"
import { useTokenIdentity } from "../helpers/useTokenIdentity"

import { TokenUnavailable } from "./shared/TokenUnavailable"

/**
 * A stat tile that takes its value as a STRING.
 *
 * Deliberately not `components/stats/Card` (`StatCard`), which takes
 * `value: number`. $TOKEN carries 18 decimal places and USD totals arrive as
 * integer cents past the safe-integer range — handing either to a numeric prop
 * rounds the figure before it is ever rendered. StatCard remains the right
 * component for counts, and is used below for exactly those.
 */
const StringStat = ({ title, value, hint }: { title: string; value: string; hint?: string }) => {
  const { darkMode } = useProps()
  return (
    <div className={clsx("rounded-lg border p-5", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
      <p className={clsx("text-xs uppercase tracking-wide", darkMode ? "text-gray-500" : "text-gray-500")}>{title}</p>
      <p className={clsx("mt-2 break-all font-mono text-2xl", darkMode ? "text-white" : "text-gray-900")}>{value}</p>
      {hint && <p className={clsx("mt-1 text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>{hint}</p>}
    </div>
  )
}

const CountStat = ({ title, value }: { title: string; value: number }) => {
  const { darkMode } = useProps()
  return (
    <div className={clsx("rounded-lg border p-5", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
      <p className={clsx("text-xs uppercase tracking-wide", darkMode ? "text-gray-500" : "text-gray-500")}>{title}</p>
      <p className={clsx("mt-2 font-mono text-2xl", darkMode ? "text-white" : "text-gray-900")}>{value.toLocaleString()}</p>
    </div>
  )
}

/** `YYYY-MM-DD` → an ISO instant, or undefined when the field is empty. */
const toIso = (day: string, endOfDay = false): string | undefined => {
  if (!day) return undefined
  const d = new Date(`${day}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

export const AnalyticsTab = ({ tenant }: { tenant: string }) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { ticker } = useTokenIdentity(tenant)
  const { darkMode } = useProps()
  const { gate, loading, run } = useTokenGate()

  const [data, setData] = useState<TokenAnalytics | null>(null)
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")

  // Caught client-side because the server rejects it outright; showing the
  // reason beats a bare failure. An inverted range is easy to type and, if it
  // silently returned zeros, would read as "no sales in this period".
  const rangeInverted = !!from && !!to && from > to

  const load = useCallback(async () => {
    if (!tenant || rangeInverted) return
    const res = await run(() => AdminTokenService.getAnalytics(tenant, toIso(from), toIso(to, true)))
    if (res) setData(res)
  }, [tenant, from, to, rangeInverted, run])

  useEffect(() => {
    load()
  }, [load])

  if (loading && !data) return <LoadingIndicator text={t("token.analyticsLoading")} />
  if (gate !== "ok") return <TokenUnavailable variant={gate} onRetry={load} />

  const dateInput = clsx("rounded-lg border px-3 py-2 text-sm outline-none", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#060715] text-white" : "border-gray-300 bg-white")

  return (
    <div className="grid gap-8">
      <section className="flex flex-wrap items-end gap-4">
        <div>
          <label className="mb-1 block text-sm font-medium">{t("token.dateFrom")}</label>
          <input type="date" value={from} max={to || undefined} onChange={(ev) => setFrom(ev.target.value)} className={dateInput} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">{t("token.dateTo")}</label>
          <input type="date" value={to} min={from || undefined} onChange={(ev) => setTo(ev.target.value)} className={dateInput} />
        </div>
        {(from || to) && (
          <button
            type="button"
            onClick={() => {
              setFrom("")
              setTo("")
            }}
            className={clsx("rounded-lg px-4 py-2 text-sm", darkMode ? "bg-[rgba(255,255,255,0.06)] text-white" : "bg-gray-100 text-gray-700")}
          >
            {t("token.clear")}
          </button>
        )}
        {rangeInverted && <span className="text-sm text-red-400">{t("token.dateRangeInverted")}</span>}
      </section>

      {!data ? (
        // An empty tenant is the NORMAL pre-launch state, not an error.
        <p className={clsx("py-12 text-center", darkMode ? "text-gray-400" : "text-gray-600")}>{t("token.noActivity", { ticker })}</p>
      ) : (
        <>
          <section>
            <h3 className="mb-4 text-lg font-semibold">{t("token.salesHeading")}</h3>
            {/* Date-filtered — see the caveat under "Staking". */}
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              <StringStat title={t("token.raised")} value={formatUsdCents(data.total_raised_usd_cents)} />
              <StringStat title={t("token.tokensSold", { ticker })} value={formatDecimalString(data.total_token_sold, 4)} />
              <StringStat title={t("token.averagePurchase")} value={formatUsdCents(data.average_purchase_usd_cents)} />
              <StringStat
                title={t("token.presaleSold")}
                value={`${formatDecimalString(data.presale_sold_percent, 4)}%`}
                hint={t("token.presaleSoldHint")}
              />
              <CountStat title={t("token.uniqueBuyers")} value={data.unique_buyers} />
              <CountStat title={t("token.purchases")} value={data.total_purchases} />
            </div>
          </section>

          <section>
            <h3 className="mb-1 text-lg font-semibold">{t("token.stakingHeading")}</h3>
            {/* The single most misreadable thing on this page if left unsaid:
                these two ignore the date filter, because "total staked between
                March and April" is not a meaningful quantity. */}
            <p className={clsx("mb-4 text-sm", darkMode ? "text-amber-400/80" : "text-amber-700")}>{t("token.stakingCaveat")}</p>
            <div className="grid gap-5 md:grid-cols-3">
              <StringStat title={t("token.currentlyStaked")} value={formatDecimalString(data.total_staked, 4)} hint={t("token.currentlyStakedHint")} />
              <CountStat title={t("token.activePositions")} value={data.active_positions} />
              <StringStat
                title={t("token.rewardsDistributed")}
                value={formatDecimalString(data.total_rewards_distributed, 4)}
                hint={t("token.rewardsDistributedHint")}
              />
            </div>
          </section>
        </>
      )}
    </div>
  )
}
